import { clearClientInstanceId, getClientInstanceId } from './clientInstance'

const AUTH_SESSION_CHANNEL_NAME = 'akive-auth-session'
const AUTH_SESSION_STORAGE_KEY = 'akive-auth-session-event'
const AUTH_SESSION_EVENT_TYPE = 'AUTH_SESSION_CHANGED'

let sessionGeneration = 0
let sessionRevision = 0
let authSession = null
let externalChannel = null
let removeExternalListener = null
let externalSessionChangeHandler = null
const sessionListeners = new Set()

function notifySessionListeners(change) {
  sessionRevision += 1
  sessionListeners.forEach((listener) => listener(change))
}

function isAuthSessionEvent(message) {
  return message?.type === AUTH_SESSION_EVENT_TYPE
}

function handleExternalSessionEvent(message) {
  if (!isAuthSessionEvent(message)) return

  if (message.action === 'cleared') {
    clearAuthSession({ announce: false })
    return
  }

  if (message.action === 'replaced') externalSessionChangeHandler?.()
}

function startExternalListener() {
  if (removeExternalListener || typeof window === 'undefined') return

  if (typeof window.BroadcastChannel === 'function') {
    try {
      externalChannel = new window.BroadcastChannel(AUTH_SESSION_CHANNEL_NAME)
      externalChannel.addEventListener('message', ({ data }) => handleExternalSessionEvent(data))
      removeExternalListener = () => {
        externalChannel?.close()
        externalChannel = null
      }
      return
    } catch {
      externalChannel = null
    }
  }

  const handleStorage = (event) => {
    if (event.key !== AUTH_SESSION_STORAGE_KEY || !event.newValue) return

    try {
      handleExternalSessionEvent(JSON.parse(event.newValue))
    } catch {
      // 다른 코드가 같은 저장소 키를 덮어써도 인증 상태는 변경하지 않는다.
    }
  }
  window.addEventListener('storage', handleStorage)
  removeExternalListener = () => window.removeEventListener('storage', handleStorage)
}

function stopExternalListener() {
  removeExternalListener?.()
  removeExternalListener = null
}

function publishSessionChange(action) {
  const message = {
    type: AUTH_SESSION_EVENT_TYPE,
    action,
    nonce: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  }

  if (externalChannel) {
    externalChannel.postMessage(message)
    return
  }

  if (typeof window === 'undefined') return

  if (typeof window.BroadcastChannel === 'function') {
    try {
      const channel = new window.BroadcastChannel(AUTH_SESSION_CHANNEL_NAME)
      channel.postMessage(message)
      channel.close()
      return
    } catch {
      // BroadcastChannel 생성이 막히면 storage 이벤트 방식으로 전환한다.
    }
  }

  try {
    window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(message))
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
  } catch {
    // 저장소 접근이 제한돼도 현재 탭의 인증 변경은 유지한다.
  }
}

function signalAuthSessionChange(action) {
  notifySessionListeners({ action, session: authSession })
  publishSessionChange(action)
}

function getAuthSession() {
  return authSession
}

function replaceAuthSession(session, { announce = true, notify = announce } = {}) {
  const safeSession = { ...session }
  delete safeSession.refreshToken
  if (!safeSession.accessToken) return clearAuthSession({ announce })

  sessionGeneration += 1
  authSession = safeSession
  if (announce) signalAuthSessionChange('replaced')
  else if (notify) notifySessionListeners({ action: 'replaced', session: authSession })
}

function clearAuthSession({ announce = true } = {}) {
  const previousSession = authSession || { clientInstanceId: getClientInstanceId() }
  sessionGeneration += 1
  authSession = null
  clearClientInstanceId()

  const change = { action: 'cleared', previousSession, session: null }
  notifySessionListeners(change)
  if (announce) publishSessionChange('cleared')
}

function captureAuthSession() {
  return {
    generation: sessionGeneration,
    session: authSession,
  }
}

function isAuthSessionCurrent({ generation }) {
  return sessionGeneration === generation
}

function setExternalSessionChangeHandler(handler) {
  externalSessionChangeHandler = handler
}

function subscribeAuthSession(listener) {
  sessionListeners.add(listener)
  startExternalListener()

  return () => {
    sessionListeners.delete(listener)
    if (sessionListeners.size === 0) stopExternalListener()
  }
}

function getAuthSessionRevision() {
  return sessionRevision
}

export {
  captureAuthSession,
  clearAuthSession,
  getAuthSession,
  getAuthSessionRevision,
  isAuthSessionCurrent,
  replaceAuthSession,
  setExternalSessionChangeHandler,
  subscribeAuthSession,
}
